// =============================================================================
// Design Library — admin API helpers (thin wrappers over @/lib/api)
// =============================================================================
// Centralizes the /admin/library/* paths + query keys so the pages stay small.
// Types come from @tkpod/shared (single source of truth with the API).
// =============================================================================

import { api } from '@/lib/api';
import type {
  LibraryAssetDetail,
  LibraryBatchFinishResponse,
  LibraryBulkUpdateResponse,
  LibraryCategoryDetail,
  LibraryCategoryNode,
  LibraryDownloadUrlResponse,
  LibrarySearchResult,
  LibrarySingleUploadInitRequest,
  LibrarySingleUploadInitResponse,
  LibraryStats,
  LibraryTagSummary,
  LibraryUploadBatchStatus,
  LibraryUploadPrecheckRequest,
  LibraryUploadPrecheckResponse,
} from '@tkpod/shared';

const BASE = '/api/v1/admin/library';

// --- query keys ------------------------------------------------------------

export const libraryKeys = {
  stats: ['library', 'stats'] as const,
  assets: (params: Record<string, unknown>) => ['library', 'assets', params] as const,
  asset: (id: string) => ['library', 'asset', id] as const,
  categories: ['library', 'categories'] as const,
  category: (id: string) => ['library', 'category', id] as const,
  tags: ['library', 'tags'] as const,
  batches: (params: Record<string, unknown>) => ['library', 'batches', params] as const,
  batch: (id: string) => ['library', 'batch', id] as const,
};

// --- reads -----------------------------------------------------------------

export function fetchStats() {
  return api.get<LibraryStats>(`${BASE}/stats`);
}

export function searchAssets(qs: string) {
  return api.get<LibrarySearchResult>(`${BASE}/assets${qs ? `?${qs}` : ''}`);
}

export function fetchAsset(id: string) {
  return api.get<LibraryAssetDetail>(`${BASE}/assets/${id}`);
}

export function fetchCategories() {
  return api.get<LibraryCategoryNode[]>(`${BASE}/categories`);
}

export function fetchCategoryDetail(id: string) {
  return api.get<LibraryCategoryDetail>(`${BASE}/categories/${id}`);
}

export function fetchTags() {
  return api.get<LibraryTagSummary[]>(`${BASE}/tags`);
}

export interface BatchListItem {
  id: string;
  name: string;
  status: LibraryUploadBatchStatus;
  totalCount: number;
  readyCount: number;
  failedCount: number;
  /** Uploader display name (resolved server-side; null if the user is gone). */
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BatchListResponse {
  items: BatchListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchBatches(qs: string) {
  return api.get<BatchListResponse>(`${BASE}/uploads${qs ? `?${qs}` : ''}`);
}

export interface BatchDetail {
  batch: {
    id: string;
    name: string;
    status: LibraryUploadBatchStatus;
    totalCount: number;
    readyCount: number;
    failedCount: number;
    /** Uploader display name (resolved server-side; null if the user is gone). */
    createdByName: string | null;
    createdAt: string;
    updatedAt: string;
  };
  assets: {
    id: string;
    fileName: string;
    sku: string;
    status: string;
    failureReason: string | null;
    thumbUrl: string | null;
    categoryId: string | null;
    categoryName: string | null;
    tags: { id: string; name: string }[];
    /** Linked primary (auto-linked at init or linked manually); null = not a variant. */
    primaryAssetId: string | null;
    /** Sku of the linked primary (resolved server-side for the review chip). */
    primarySku: string | null;
    /** Sku-suffix variant suggestion (approve = PATCH primaryAssetId). */
    variantSuggestion: {
      primaryAssetId: string;
      primarySku: string;
      label: string;
    } | null;
  }[];
}

export function fetchBatch(id: string) {
  return api.get<BatchDetail>(`${BASE}/uploads/${id}`);
}

// --- writes ----------------------------------------------------------------

export function patchAsset(id: string, body: Record<string, unknown>) {
  return api.patch<LibraryAssetDetail>(`${BASE}/assets/${id}`, body);
}

export function bulkUpdate(body: Record<string, unknown>) {
  return api.post<LibraryBulkUpdateResponse>(`${BASE}/assets/bulk-update`, body);
}

/** Permanently delete only the FAILED assets among the given ids. */
export function bulkDeleteFailed(assetIds: string[]) {
  return api.post<{ deleted: number }>(`${BASE}/assets/bulk-delete-failed`, { assetIds });
}

export function archiveAsset(id: string, opts: { withVariants?: boolean; force?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (opts.withVariants) qs.set('withVariants', 'true');
  if (opts.force) qs.set('force', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.delete<{ archived: number; hardDeleted?: boolean }>(`${BASE}/assets/${id}${suffix}`);
}

export function assetDownloadUrl(id: string, variant?: 'working' | 'original', version?: number) {
  const qs = new URLSearchParams();
  if (variant) qs.set('variant', variant);
  if (version) qs.set('version', String(version));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.post<LibraryDownloadUrlResponse>(`${BASE}/assets/${id}/download-url${suffix}`, {});
}

export function reprocessAsset(id: string) {
  return api.post<{ assetId: string; status: string }>(`${BASE}/assets/${id}/reprocess`, {});
}

/** Start a new version: returns a presigned PUT URL for the replacement bytes. */
export function replaceAsset(id: string) {
  return api.post<{ assetId: string; version: number; originalKey: string; uploadUrl: string }>(
    `${BASE}/assets/${id}/replace`,
    {},
  );
}

/** Finalize a single-asset version upload (the replace flow). */
export function completeAssetVersion(assetId: string, version: number) {
  return api.post<{ batchId: string; results: unknown[] }>(`${BASE}/uploads/complete`, { assetId, version });
}

export function finishBatch(id: string) {
  return api.post<LibraryBatchFinishResponse>(`${BASE}/uploads/${id}/finish`, {});
}

/** Pre-upload conflict/dup/variant dry run (no bytes involved). */
export function precheckUpload(body: LibraryUploadPrecheckRequest) {
  return api.post<LibraryUploadPrecheckResponse>(`${BASE}/uploads/precheck`, body);
}

/** Init a single batchless upload (Assets page quick upload / variant upload). */
export function singleUploadInit(body: LibrarySingleUploadInitRequest) {
  return api.post<LibrarySingleUploadInitResponse>(`${BASE}/uploads/single-init`, body);
}

// categories
export function createCategory(body: Record<string, unknown>) {
  return api.post<LibraryCategoryNode>(`${BASE}/categories`, body);
}
export function updateCategory(id: string, body: Record<string, unknown>) {
  return api.patch<LibraryCategoryNode>(`${BASE}/categories/${id}`, body);
}
export function deleteCategory(id: string) {
  return api.delete<{ deleted: true; unlinkedTags: number }>(`${BASE}/categories/${id}`);
}

// category ↔ tag links
export function linkCategoryTag(categoryId: string, tagId: string) {
  return api.post<LibraryCategoryDetail>(`${BASE}/categories/${categoryId}/tags`, { tagId });
}
export function unlinkCategoryTag(categoryId: string, tagId: string) {
  return api.delete<LibraryCategoryDetail>(`${BASE}/categories/${categoryId}/tags/${tagId}`);
}

// tags
export function createTag(body: Record<string, unknown>) {
  return api.post<LibraryTagSummary>(`${BASE}/tags`, body);
}
export function updateTag(id: string, body: Record<string, unknown>) {
  return api.patch<LibraryTagSummary>(`${BASE}/tags/${id}`, body);
}
export function deleteTag(id: string) {
  return api.delete<{ deleted: true; removedFrom: number; unlinkedCategories: number }>(
    `${BASE}/tags/${id}`,
  );
}

/** Flatten the category tree into label-path options for selects. */
export function flattenCategories(
  nodes: LibraryCategoryNode[],
  depth = 0,
): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (const n of nodes) {
    out.push({ value: n.id, label: `${'  '.repeat(depth)}${n.name}` });
    if (n.children?.length) out.push(...flattenCategories(n.children, depth + 1));
  }
  return out;
}
